import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiBookOpen, FiVideo, FiPlay, FiChevronLeft, FiCheckCircle } from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { dashboardAPI } from '../../services/api';

const Activity = () => {
  const { user } = useAuth();
  const { showError } = useNotification();
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadActivity = async () => {
      try {
        setLoading(true);
        const response = await dashboardAPI.getDashboardData({ role: user?.role });
        const payload = response?.data?.data || response?.data || response;
        const recent = Array.isArray(payload?.recentActivity) ? payload.recentActivity : [];
        setActivity(recent);
      } catch (err) {
        showError('Failed to load recent activity');
      } finally {
        setLoading(false);
      }
    };
    loadActivity();
  }, [user, showError]);

  const backPath = user?.role === 'admin' ? '/admin/analytics' : '/analytics';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Recent Activity</h1>
            <p className="text-sm text-slate-500 mt-0.5">A full list of your latest tests and sessions.</p>
          </div>
          <Link to={backPath} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
            <FiChevronLeft className="h-4 w-4" />
            Back to Analytics
          </Link>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {activity.length === 0 ? (
            <div className="text-center py-14 px-6">
              <FiBookOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No recent activity found.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {activity.map((item) => (
                <li key={item.id || `${item.type}-${item.time}-${item.title}`} className="p-4 flex items-center gap-3 hover:bg-slate-50/50 transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                    {item.type === 'test' && <FiBookOpen className="h-5 w-5" />}
                    {item.type === 'session' && <FiVideo className="h-5 w-5" />}
                    {item.type === 'game' && <FiPlay className="h-5 w-5" />}
                    {!['test', 'session', 'game'].includes(item.type) && <FiCheckCircle className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900">{item.title}</div>
                    <div className="text-sm text-slate-500 flex flex-wrap gap-x-3 gap-y-0 mt-0.5">
                      {item.score != null && <span>Score: {item.score}%</span>}
                      {item.attendees != null && <span>{item.attendees} attendees</span>}
                      {item.count != null && <span>{item.count} items</span>}
                      {item.status && <span>Status: {item.status}</span>}
                      {item.time && <span>{item.time}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Activity;
